import { v4 as uuidV4 } from 'uuid'
import axios from 'axios'

import { makeAutoObservable, runInAction, toJS } from 'mobx'
import storeUser from 'GlobalStores/User/StoreUser'
import mixpanelCookieService from 'Services/Cookie/MixpanelCookieService'
import { ServerEnv } from 'Configs/ServerEnv'

const MIXPANEL_EVENT_NAME = {
    CREATE_ACCOUNT_COMPLETED: 'fga_create_account_completed',
    INITIALIZE_USER_PROFILE_FOR_OLD_USERS: 'fga_initialize_user_profile_for_old_users',
    LANGUAGE_CHANGED: 'fga_user_language_changed',
    LOGOUT: 'fga_logout',
    PAGE_VIEW: 'fga_page_view',
    SIGN_IN_COMPLETED: 'fga_sign_in_completed',
    INITIALIZE_USER_PROFILE_FOR_OLD_USERS: 'fga_initialize_user_profile_for_old_users'
}

class MixpanelService {
    UNICITY_PRODUCT = 'feel_great_asia'

    UTM_KEYS = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_content', 'utm_term', 'utm_id', 'utm_source_platform', 'utm_campaign_id', 'utm_creative_format', 'utm_marketing_tactic']

    previousPath = ''
    superProperties = {}
    deviceId = null
    unicityId = null
    isInitialized = false

    utmParameters = {}

    initialize() {
        this.deviceId = this.initializeDeviceId()
        this.initializeSuperProperties()
        this.setUtmParameters()
        this.isInitialized = true
    }

    async initializeDeviceId() {
        const storedDeviceId = mixpanelCookieService.getDeviceId()

        if (storedDeviceId) {
            return storedDeviceId
        }

        const newDeviceId = uuidV4()

        try {
            mixpanelCookieService.setDeviceId(newDeviceId)
        } catch (error) {
            console.error('Failed to set device_id cookie:', error)
        }

        return newDeviceId
    }

    registerSuperProperties(properties) {
        // console.log('registerSuperProperties', properties)
        this.superProperties = { ...this.superProperties, ...properties }
    }

    initializeSuperProperties() {
        if (typeof window === 'undefined') return

        if (!localStorage.getItem('login-extends')) {
            this.resetIdentity()
        }

        this.registerSuperProperties({
            unicity_product: this.UNICITY_PRODUCT,
            user_market_extended: mixpanelCookieService.getCountry(),
            user_language: mixpanelCookieService.getLanguage(),
            is_user_logged_in: mixpanelCookieService.getIsUserLoggedIn()
        })

        if (mixpanelCookieService.getUserType()) {
            this.registerSuperProperties({
                user_type: mixpanelCookieService.getUserType()
            })
        }

        if (mixpanelCookieService.getIsUserLoggedIn() === 'true' && mixpanelCookieService.getUnicityId()) {
            this.setUnicityId(mixpanelCookieService.getUnicityId())
        }
    }

    async sendToApi({ action, event, properties }) {
        return
        // try {
        //     let url = `${ServerEnv.GetMemberCalls2UrlFromDomain()}/mixpanel`
        //     // let url = "http://localhost:8081/mixpanel"
        //     url += action === 'trackEvent' ? '/trackEvent' : '/setPeopleProperties'

        //     // console.log("[DEBUG VIEW 👀] sendToApi ,", JSON.stringify({
        //     //   action,
        //     //   event,
        //     //   properties,
        //     //   unicityProduct: this.UNICITY_PRODUCT,
        //     // }, null, 2))
        //     // return

        //     await axios(url, {
        //         method: 'POST',
        //         headers: {
        //             'Content-Type': 'application/json'
        //         },
        //         data: {
        //             action,
        //             event,
        //             properties,
        //             unicityProduct: this.UNICITY_PRODUCT
        //         }
        //     })
        // } catch (error) {
        //     console.error(`Failed to ${action}:`)
        // }
    }

    getBrowserMetadata() {
        if (typeof window === 'undefined') return {}

        return {
            $screen_height: window.screen?.height,
            $screen_width: window.screen?.width,
            $current_url: window.location?.href
        }
    }

    async trackEvent(event, properties = {}) {
        const deviceId = await Promise.resolve(this.deviceId)

        const eventProperties = {
            ...this.superProperties,
            ...(this.unicityId && { $user_id: this.unicityId }),
            ...this.getBrowserMetadata(),
            ...this.getInitialReferrerProperties(),
            ...this.getUtmParameters(),
            ...this.getReferrerData(),
            $device_id: deviceId,
            ...properties
        }

        // console.log('trackEvent fn -> eventProperties', eventProperties)

        await this.sendToApi({
            action: 'trackEvent',
            event,
            properties: eventProperties
        })
    }

    resetSuperProperties() {
        this.superProperties = {}
    }

    setUnicityId(unicityId) {
        this.unicityId = unicityId
    }

    async setPeopleProperties({ properties, event }) {
        // console.log('setPeopleProperties', properties)
        if (!this.unicityId) {
            throw new Error('Cannot set people properties without a user ID.')
        }

        await this.sendToApi({
            action: 'setPeopleProperties',
            event,
            properties: properties
        })
    }

    getInitialReferrerProperties() {
        const initialReferrer = mixpanelCookieService.getInitialReferrer()
        const initialReferringDomain = mixpanelCookieService.getInitialReferringDomain()

        return {
            $initial_referrer: initialReferrer || '$direct',
            $initial_referring_domain: initialReferringDomain || '$direct'
        }
    }

    setUtmParameters() {
        if (typeof window === 'undefined') return {}

        const url = new URL(window.location.href)
        const params = {}

        for (const param of this.UTM_KEYS) {
            const value = url.searchParams.get(param)
            if (value) {
                params[param] = value
            }
        }

        this.utmParameters = params
        return
    }

    getUtmParameters() {
        if (typeof window === 'undefined') return {}

        const url = new URL(window.location.href)
        const params = {}

        for (const param of this.UTM_KEYS) {
            const value = url.searchParams.get(param)
            if (value) {
                params[param] = value
            }
        }

        return params
    }

    parseReferrerUrl(url) {
        try {
            return {
                $referrer: url,
                $referring_domain: new URL(url).host
            }
        } catch {
            console.error('Failed to parse referrer url:', url)
            return {}
        }
    }

    getNaturalNavigationReferrer() {
        // console.log("[DEBUG VIEW 👀] getNaturalNavigationReferrer", document.referrer)
        if (!document.referrer)
            return {
                $referrer: '$direct',
                $referring_domain: '$direct'
            }
        return this.parseReferrerUrl(document.referrer)
    }

    getProgrammaticNavigationReferrer() {
        // console.log("[DEBUG VIEW 👀] getProgrammaticNavigationReferrer", toJS(this.previousPath))
        if (!this.previousPath) return false
        return this.parseReferrerUrl(this.previousPath)
    }

    getReferrerData() {
        if (this.getProgrammaticNavigationReferrer()) return this.getProgrammaticNavigationReferrer()

        if (this.getNaturalNavigationReferrer()) return this.getNaturalNavigationReferrer()

        return {}
    }

    async resetIdentity() {
        this.resetSuperProperties()

        this.unicityId = null

        mixpanelCookieService.clearWhenUnAuthorized()
    }

    async handleTrackPageViewEvent() {
        if (!('unicity_product' in this.superProperties)) {
            this.initializeSuperProperties()
        }

        if (this.isInitialized) {
            const isInitialWithManyRedirects = mixpanelCookieService.getIsInitialWithManyRedirects()

            const properties = isInitialWithManyRedirects
                ? {
                    $referrer: mixpanelCookieService.getInitialReferrer() || '$direct',
                    $referring_domain: mixpanelCookieService.getInitialReferringDomain() || '$direct'
                }
                : {}

            this.trackEvent(MIXPANEL_EVENT_NAME.PAGE_VIEW, properties)
                .then(() => {
                    mixpanelCookieService.removeIsInitialWithManyRedirects()
                })
                .catch(() => { })
        }
    }

    async handleLanguageChangedEvent({ newLanguage, oldLanguage }) {
        const newLanguageLowerCase = newLanguage.toLowerCase()
        const oldLanguageLowerCase = oldLanguage.toLowerCase()

        mixpanelCookieService.setLanguage(newLanguageLowerCase)

        this.registerSuperProperties({
            user_language: newLanguageLowerCase
        })

        if (oldLanguageLowerCase === newLanguageLowerCase) {
            return
        }

        await this.trackEvent(MIXPANEL_EVENT_NAME.LANGUAGE_CHANGED, {
            previous_user_language: oldLanguageLowerCase
        }).catch(() => { })
    }

    async handleCreateAccountCompletedEvent({ unicityId, isAgreeExclusiveOffers, event_location }) {
        const EVENT_NAME = MIXPANEL_EVENT_NAME.CREATE_ACCOUNT_COMPLETED

        isAgreeExclusiveOffers = isAgreeExclusiveOffers && typeof isAgreeExclusiveOffers === 'boolean' ? isAgreeExclusiveOffers.toString() : 'false'

        mixpanelService.setUnicityId(unicityId)

        const customerData = storeUser.userData

        const { enroller = {}, sponsor = {}, type: userType, market } = customerData || {}

        const enrollerId = (enroller.id?.unicity || '').toString()
        const sponsorId = (sponsor.id?.unicity || '').toString()

        mixpanelService.registerSuperProperties({
            is_user_logged_in: 'true',
            user_type: userType
        })

        mixpanelCookieService.setIsUserLoggedIn('true')
        mixpanelCookieService.setUserType(userType)
        mixpanelCookieService.setUnicityId(unicityId)

        const peopleProperties = {
            unicity_id: unicityId,
            enroller_id: enrollerId,
            sponsor_id: sponsorId || enrollerId,
            user_market_extended: market,
            user_language: mixpanelCookieService.getLanguage(),
            membership_registration_date: new Date().toISOString(),
            consent_exclusive_offers: isAgreeExclusiveOffers.toString(),
            user_type: userType
        }

        await this.setPeopleProperties({
            event: EVENT_NAME,
            properties: peopleProperties
        })
            .then(() => {
                mixpanelCookieService.setUserInitializedInMixpanel('true')
            })
            .catch(() => { })

        await this.trackEvent(EVENT_NAME, {
            event_location,
            consent_exclusive_offers: isAgreeExclusiveOffers.toString()
        })
    }

    async handleSignInCompletedEvent({ unicityId }) {
        if (mixpanelCookieService.getUserInitializedInMixpanel() === 'true') {
            return
        }

        try {
            const EVENT_NAME = MIXPANEL_EVENT_NAME.SIGN_IN_COMPLETED

            mixpanelService.setUnicityId(unicityId)

            let event_location = ''

            if (window.location.pathname.includes('login')) {
                event_location = 'account_login'
            } else if (window.location.pathname.includes('checkout')) {
                event_location = 'checkout'
            }

            const customerData = storeUser.userData

            const { enroller = {}, sponsor = {}, joinDate, type: userType } = customerData || {}

            mixpanelService.registerSuperProperties({
                is_user_logged_in: 'true',
                user_type: userType
            })

            mixpanelCookieService.setIsUserLoggedIn('true')
            mixpanelCookieService.setUserType(userType)
            mixpanelCookieService.setUnicityId(unicityId)

            const enrollerId = (enroller.id?.unicity || '').toString()
            const sponsorId = (sponsor.id?.unicity || '').toString()

            const peopleProperties = {
                unicity_id: unicityId,
                enroller_id: enrollerId,
                sponsor_id: sponsorId || enrollerId,
                user_market_extended: mixpanelCookieService.getCountry(),
                user_language: mixpanelCookieService.getLanguage(),
                membership_registration_date: joinDate,
                user_type: mixpanelCookieService.getUserType() || userType
            }

            await this.setPeopleProperties({
                event: EVENT_NAME,
                properties: peopleProperties
            })
                .then(() => {
                    mixpanelCookieService.setUserInitializedInMixpanel('true')
                })
                .catch(() => { })

            await this.trackEvent(EVENT_NAME, {
                event_location
            })
        } catch (error) {
            console.error('Failed to handleSignInCompletedEvent:', error)
        }
    }

    async handleActionAfterLogout() {
        this.resetSuperProperties()

        this.unicityId = null

        mixpanelCookieService.clearWhenUserLoggedOut()

        this.deviceId = await this.initializeDeviceId()
    }

    async handleLogoutEvent() {
        const EVENT_NAME = MIXPANEL_EVENT_NAME.LOGOUT

        this.registerSuperProperties({
            is_user_logged_in: 'false'
        })

        await this.trackEvent(EVENT_NAME).catch(() => { })

        await this.handleActionAfterLogout()
    }

    //initialize_user_profile_for_old_users
    async handleInitializeUserProfileForOldUsers({ unicityId }) {
        if (mixpanelCookieService.getUserInitializedInMixpanel() === 'true') {
            // console.log('Already initialized Mixpanel')
            return
        }

        try {
            const EVENT_NAME = MIXPANEL_EVENT_NAME.INITIALIZE_USER_PROFILE_FOR_OLD_USERS

            mixpanelService.setUnicityId(unicityId)

            const customerData = storeUser.userData

            const { enroller = {}, sponsor = {}, joinDate, type: userType } = customerData || {}

            mixpanelService.registerSuperProperties({
                is_user_logged_in: 'true',
                user_type: userType
            })

            mixpanelCookieService.setIsUserLoggedIn('true')
            mixpanelCookieService.setUserType(userType)
            mixpanelCookieService.setUnicityId(unicityId)

            const enrollerId = (enroller.id?.unicity || '').toString()
            const sponsorId = (sponsor.id?.unicity || '').toString()

            const peopleProperties = {
                unicity_id: unicityId,
                enroller_id: enrollerId,
                sponsor_id: sponsorId || enrollerId,
                user_market_extended: mixpanelCookieService.getCountry(),
                user_language: mixpanelCookieService.getLanguage(),
                membership_registration_date: joinDate,
                user_type: mixpanelCookieService.getUserType() || userType
            }

            await this.setPeopleProperties({
                event: EVENT_NAME,
                properties: peopleProperties
            })
                .then(() => {
                    mixpanelCookieService.setUserInitializedInMixpanel('true')
                })
                .catch(() => { })

            await this.trackEvent(EVENT_NAME)
        } catch (error) {
            console.error('Failed to handleInitializeUserProfileForOldUsers:', error)
        }
    }

    constructor() {
        makeAutoObservable(this)

        // runInAction(() => {
        //   this.deviceId = this.initializeDeviceId()
        //   this.initialize()
        // })
    }
}

const mixpanelService = new MixpanelService()

export default mixpanelService
